import { useCallback, type ReactNode } from 'react'
import { CompliancePoliciesList, EmptyCompliancePolicies, ButtonAdd, NotReady, Page } from '@components'
import { useCompliancePolicies, useTranslation } from '@front/model'
import { useQueryDisclosure } from '@hooks'
import { Switch } from '@mantine/core'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { isNotEmpty, type WithCompliancePolicyId } from '@terros/common'
import { useQueryState, parseAsStringLiteral } from 'nuqs'

const ACTIONS = ['edit', 'view'] as const

function CompliancePolicies(): ReactNode {
  const [action] = useQueryState('action', parseAsStringLiteral(ACTIONS).withDefault(ACTIONS[0]))
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [showArchived, { toggle }] = useQueryDisclosure('showArchived')
  const {
    data: policies,
    refetch,
    isFetching,
    isLoading,
    error,
  } = useCompliancePolicies({ archived: showArchived ? 'all' : false })

  const onPolicyClick = useCallback(
    ({ policyId }: WithCompliancePolicyId): void => {
      if (action === 'view') {
        navigate({ to: `/compliance/violations/${policyId}` })
        return
      }
      navigate({ to: `/compliance/policy/${policyId}` })
    },
    [action, navigate]
  )

  if (isLoading) return <NotReady data={policies} error={error} id='Compliance Policies' isLoading={isLoading} />

  return (
    <Page>
      <Page.Heading title={t('Compliance Policies')} back isFetching={isFetching} refetch={refetch}>
        <Switch checked={showArchived} label='Show Archived' labelPosition='left' onChange={toggle} />
        {action === 'edit' && <ButtonAdd onClick={() => navigate({ to: 'add' })} />}
      </Page.Heading>
      <Page.Content scrollArea>
        {isNotEmpty(policies) ? (
          <CompliancePoliciesList policies={policies} onClick={onPolicyClick} />
        ) : (
          <EmptyCompliancePolicies />
        )}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/compliance_/policy/')({
  component: CompliancePolicies,
})
